<template>
  <div id="user-list">
    <v-card>
      <v-card-title>
        {{ $t("Tiedostot") }}
        <v-spacer></v-spacer>

      </v-card-title>
      <!-- actions -->
      <v-card-text class="d-flex align-center flex-wrap pb-0">

        <span>
          {{ $t('Ryhmän ylläpitäjä näkee vain ylläpitämiensä ryhmien tiedostot') }}
        </span>
        <v-spacer></v-spacer>

        <div class="d-flex align-center flex-wrap gap-2">
          <v-btn v-if="tenant.settings.acknowledgments_enabled" color="secondary" class="mb-4 mr-4"
            :to="{ name: 'admin-resources-stats-overview' }">
            <span>{{ $t("Lukukuittaukset") }}</span>
          </v-btn>
          <v-btn v-if="user.is_tenant_admin && breadcrumbTrail.length < 2" color="primary" class="mb-4 mr-2 lighten-4"
            @click="showFolderDialog = true">
            <v-icon left>{{ icons.mdiFolderOutline }}</v-icon>
            <span>{{ currentFolderId ? $t("Uusi alikansio") : $t("Uusi kansio") }}</span>
          </v-btn>
          <v-btn color="primary" class="mb-4" :to="{ name: 'admin-resource-form', query: { folder: currentFolderId } }">
            <span>{{ $t("Uusi tiedosto") }}</span>
          </v-btn>
        </div>
      </v-card-text>

      <!-- Breadcrumb Navigation -->
      <v-card-text v-if="!initializing" class="py-2">
        <div class="d-flex align-center flex-wrap">
          <v-icon size="20" class="mr-2">{{ icons.mdiFolderOutline }}</v-icon>
          <span class="text-body-2 cursor-pointer" :class="{ 'primary--text': breadcrumbTrail.length > 0 }"
            @click="showAllResources">
            {{ $t("Kaikki tiedostot") }}
          </span>
          <template v-for="(folder, index) in breadcrumbTrail">
            <span :key="`breadcrumb-${index}-icon`">
              <v-icon size="16" class="mx-2">{{ icons.mdiChevronRight }}</v-icon>
              <span class="text-body-2"
                :class="index === breadcrumbTrail.length - 1 ? 'font-weight-medium' : 'cursor-pointer primary--text'"
                @click="index !== breadcrumbTrail.length - 1 ? navigateToBreadcrumb(index) : null">
                {{ folder.title }}
              </span>
            </span>
          </template>
        </div>
      </v-card-text>


      <v-data-table v-if="!initializing" :headers="tableColumns" :items="combinedItems"
        :server-items-length="totalCount" :items-per-page="10" :options.sync="options" :loading="loading"
        mobile-breakpoint="0" :footer-props="{
          'items-per-page-options': [10],
        }">
        <!-- title -->
        <template #[`item.title`]="{ item }">
          <div class="d-flex align-center">
            <template v-if="item.isFolder">
              <v-icon color="primary" class="mr-2">{{ icons.mdiFolderOutline }}</v-icon>
              <span class="font-weight-medium cursor-pointer" @click="openFolder(item.id, item.title)">
                {{ item.title }}
              </span>
            </template>
            <template v-else>
              <router-link :to="{ name: 'admin-resource-form', params: { id: item.id } }"
                class="font-weight-medium text-decoration-none">
                {{ item.title }}
              </router-link>
            </template>
          </div>
        </template>

        <template #[`item.description`]="{ item }">
          <span v-if="item.isFolder">
            {{ item.resource_count }} {{ $t("tiedostoa") }}
          </span>
          <span v-else>
            {{ item.description }}
          </span>
        </template>

        <template #[`item.resource_type`]="{ item }">
          <span v-if="item.isFolder">
            {{ $t("Kansio") }}
          </span>
          <span v-else-if="item.resource_type == resource_types.document.value">
            {{ $t("Tiedosto") }}
          </span>
          <span v-else>
            {{ $t("Linkki") }}
          </span>
        </template>

        <!-- group -->
        <template #[`item.group_name`]="{ item }">
          <v-chip v-if="!item.isFolder" label small color="primary">{{ item.group_name }}</v-chip>
        </template>

        <!-- published -->
        <template #[`item.published`]="{ item }">
          <div v-if="!item.isFolder" class="text-left">
            <v-icon v-if="item.published" size="24" color="success">{{ icons.mdiCheckboxMarkedCircle }}
            </v-icon>
            <v-icon v-else size="24" color="error">{{ icons.mdiCloseCircle }}
            </v-icon>
          </div>
        </template>

        <template #[`item.acknowledgements`]="{ item }">
          <template v-if="tenant.settings.acknowledgments_enabled && item.require_acknowledgment">
            <v-btn icon @click="showAcknowledgments(item)">
              <v-icon>{{ icons.mdiListStatus }}</v-icon>
            </v-btn>
          </template>
        </template>


        <!-- open -->
        <template #[`item.open`]="{ item }">
          <a v-if="item.isFolder" @click.stop="editFolder(item)" class="cursor-pointer">
            {{ $t("Muokkaa") }} &raquo;
          </a>
          <a v-else-if="item.resource_type == resource_types.document.value" @click="view(item)">
            {{ $t("Avaa") }} &raquo;
          </a>
          <a v-else :href="item.link" target="_blank">{{ $t("Avaa") }} &raquo;
          </a>
        </template>
      </v-data-table>
      <div class="pa-4" v-if="initializing">
        <v-skeleton-loader type="table-row-divider, table-row@10"></v-skeleton-loader>
      </div>
    </v-card>

    <!-- Folder Form Dialog -->
    <FolderFormDialog :show="showFolderDialog" :mode="folderMode" :folder="editingFolder"
      :parent-folder-id="currentFolderId" @save="handleFolderSave" @cancel="handleFolderCancel"
      @close="handleFolderCancel" @delete="handleFolderDelete" />

    <v-dialog v-model="acknowledgmentsDialogVisible" max-width="1200px" scrollable>
      <v-card style="min-height: 75vh;">
        <v-card-text class="pa-0">
          <Acknowledgments :target-type="AcknowledgmentTypes.Resource" :target-id="acknowledgmentResourceId"
            :title="acknowledgmentResourceTitle" :version="acknowledgmentResourceVersion"
            :key="`ack-res-${acknowledgmentResourceId}`">
          </Acknowledgments>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>


<script>
import { onMounted, ref, watch, computed } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { mdiCheckboxMarkedCircle, mdiListStatus, mdiCloseCircle, mdiPlus, mdiFolderOutline, mdiChevronRight, mdiArrowLeft, mdiPencil } from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import resource_types from "@core/utils/resource_types";
import download from "downloadjs";
import FolderFormDialog from "@/components/resources/FolderFormDialog.vue";
import useAuth from "@/useAuth";
import Acknowledgments from "@/components/acknowledgements/Acknowledgments.vue"
import AcknowledgmentTypes from "@/@core/utils/acknowledgment_types";

export default {
  components: {
    FolderFormDialog,
    Acknowledgments
  },
  setup() {
    const { user, tenant } = useAuth();
    const resources = ref([]);
    const folders = ref([]);
    const totalCount = ref(0);
    const breadcrumbTrail = ref([]);

    const page = ref(1);
    const options = ref({});
    const loading = ref(false);
    const initializing = ref(true);
    const showFolderDialog = ref(false);
    const editingFolder = ref(null);
    const folderMode = ref("create");
    const acknowledgmentsDialogVisible = ref(false);
    const acknowledgmentResourceId = ref(null);
    const acknowledgmentResourceTitle = ref(null);
    const acknowledgmentResourceVersion = ref(null);

    const { t } = useTranslations();

    const tableColumns = [
      { text: t("Nimi").toUpperCase(), value: "title", sortable: false },
      {
        text: t("Kuvaus").toUpperCase(),
        value: "description",
        sortable: false,
      },
      {
        text: t("Tyyppi").toUpperCase(),
        value: "resource_type",
        sortable: false,
      },
      { text: t("Ryhmä").toUpperCase(), value: "group_name", sortable: false },
      {
        text: t("Julkaistu").toUpperCase(),
        value: "published",
        sortable: false,
      },
      {
        text: "",
        value: "acknowledgements",
        sortable: false,
      },
      {
        text: "",
        value: "open",
        sortable: false,
      },
    ];

    // Computed property for current folder ID (last item in breadcrumb trail)
    const currentFolderId = computed(() => {
      return breadcrumbTrail.value.length > 0
        ? breadcrumbTrail.value[breadcrumbTrail.value.length - 1].id
        : null;
    });

    // fetch data
    const fetchResources = async () => {
      loading.value = true;
      const response = await adminApi.getResources(page.value, 10, currentFolderId.value);

      // Extract folders and resources from the new response structure
      folders.value = response.data.folders || [];
      resources.value = response.data.resources?.results || [];
      totalCount.value = (response.data.resources?.count || 0) + folders.value.length;

      loading.value = false;
      initializing.value = false;
    };

    // Open a folder
    const openFolder = (folderId, folderTitle) => {
      breadcrumbTrail.value.push({ id: folderId, title: folderTitle });
      page.value = 1;
      options.value = { ...options.value, page: 1 };
      fetchResources();
    };

    // Navigate to a specific breadcrumb level
    const navigateToBreadcrumb = (index) => {
      breadcrumbTrail.value = breadcrumbTrail.value.slice(0, index + 1);
      page.value = 1;
      options.value = { ...options.value, page: 1 };
      fetchResources();
    };

    // Show all resources (clear folder filter)
    const showAllResources = () => {
      breadcrumbTrail.value = [];
      page.value = 1;
      options.value = { ...options.value, page: 1 };
      fetchResources();
    };

    watch(options, () => {
      if (page.value !== options.value.page) {
        page.value = options.value.page;
        fetchResources();
      }
    });

    onMounted(() => {
      fetchResources();
    });

    const view = async (resource) => {
      const response = await adminApi.downloadResource(resource.id);
      if (response.ok) {
        download(response.data, resource.filename);
      } else {
        alert(response.error);
      }
    };

    // Combine folders and resources for the table
    const combinedItems = computed(() => {
      const resourceItems = resources.value.map(resource => ({
        ...resource,
        isFolder: false,
      }));

      if (page.value === 1) {
        const folderItems = folders.value.map(folder => ({
          ...folder,
          isFolder: true,
        }));

        return [...folderItems, ...resourceItems];
      } else {
        return resourceItems;
      }
    });



    // Edit folder
    const editFolder = async (folder) => {
      const res = await adminApi.getResourceFolder(folder.id);
      if (res.ok) {
        editingFolder.value = res.data;
        folderMode.value = "edit";
        showFolderDialog.value = true;
      }
    };

    // Folder dialog handlers
    const handleFolderSave = (savedFolder) => {
      showFolderDialog.value = false;
      editingFolder.value = null;
      folderMode.value = "create";
      // Refresh the list to show the new/updated folder
      fetchResources();
    };

    const handleFolderCancel = () => {
      showFolderDialog.value = false;
      editingFolder.value = null;
      folderMode.value = "create";
    };

    const handleFolderDelete = () => {
      showFolderDialog.value = false;
      editingFolder.value = null;
      folderMode.value = "create";
      // Refresh the list after deletion
      fetchResources();
    };

    const showAcknowledgments = (item) => {
      acknowledgmentResourceId.value = item.id;
      acknowledgmentResourceVersion.value = item.acknowledgment_version;
      acknowledgmentResourceTitle.value = item.title;
      acknowledgmentsDialogVisible.value = true;
    }

    return {
      acknowledgmentsDialogVisible,
      acknowledgmentResourceTitle,
      acknowledgmentResourceVersion,
      acknowledgmentResourceId,
      showAcknowledgments,
      resources,
      folders,
      totalCount,
      combinedItems,
      currentFolderId,
      breadcrumbTrail,
      tableColumns,
      tenant,
      options,
      loading,
      initializing,
      showFolderDialog,
      editingFolder,
      folderMode,
      resource_types,
      user,
      AcknowledgmentTypes,
      view,
      openFolder,
      navigateToBreadcrumb,
      showAllResources,
      editFolder,
      handleFolderSave,
      handleFolderCancel,
      handleFolderDelete,
      icons: {
        mdiCheckboxMarkedCircle,
        mdiListStatus,
        mdiCloseCircle,
        mdiPlus,
        mdiFolderOutline,
        mdiChevronRight,
        mdiArrowLeft,
        mdiPencil,
      },
      t,
    };
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
